import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { hasScope } from '@nyce/admin-kit/api';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  Tabs,
  useAsync,
  useNotice,
} from '../../components/common';
import { SCOPES } from '../../api';
import { useHubLogin } from '../../session';
import { InstallationMarks } from './InstallationMarks';
import { stats, type Diagnostics, type SystemSeries } from '../../api/stats';
import { formatDateTime, formatRelative } from '../../lib/format';
import { EnvLinks } from './EnvLinks';
import { DatabaseDiagnostics } from './DatabaseDiagnostics';
import { LineChart, type Marker } from '@nyce/admin-kit/console';
import { envsForInstallation, useStatsLinks } from './links';
import { ErrorGroupsSection, PerformanceSection, VolumesSection } from './sections';
import {
  IngestStatus,
  OrgLink,
  RangeTabs,
  fmtMs,
  rangeWindow,
  statName,
  statsError,
  useParam,
  useRange,
} from './shared';
import './statistics.css';

type Tab = 'performance' | 'resources' | 'regressions' | 'errors' | 'volumes' | 'diagnostics' | 'versions';
const TABS: { value: Tab; label: string }[] = [
  { value: 'performance', label: 'Performance' },
  { value: 'resources', label: 'Resources' },
  { value: 'regressions', label: 'Regressions' },
  { value: 'errors', label: 'Errors' },
  { value: 'volumes', label: 'Volumes' },
  { value: 'diagnostics', label: 'Diagnostics' },
  { value: 'versions', label: 'Versions' },
];

const num = (v: number) =>
  Math.abs(v) >= 1000 ? Math.round(v).toLocaleString() : String(Number(v.toFixed(2)));

/** WMS version per day from the diagnostics snapshots (newest first) → the changes, oldest first. */
export function versionChanges(list: Diagnostics[]): { at: string; from: string | null; to: string }[] {
  const days = [...list]
    .filter((d) => d.version && d.created)
    .sort((a, b) => a.created!.localeCompare(b.created!));
  const out: { at: string; from: string | null; to: string }[] = [];
  let prev: string | null = null;
  for (const d of days) {
    if (d.version !== prev) {
      if (prev !== null) out.push({ at: d.created!, from: prev, to: d.version! });
      prev = d.version!;
    }
  }
  return out;
}

/** `Sys:` resource stats of one WorkServer (CPU, memory, GC, thread pool, …): one chart per stat, avg and max. */
function Resources({ customerId, workserver }: { customerId: number; workserver: string }) {
  const [range, setRange] = useRange('24h');
  const w = useMemo(() => rangeWindow(range), [range]);
  const available = useAsync(
    () => stats.systemStats({ customerId, workserver, from: w.fromDay, to: w.toDay }),
    [customerId, workserver, w.fromDay, w.toDay]
  );
  const names = (available.data ?? []).map((s) => s.stat).slice(0, 20);
  const series = useAsync(
    () =>
      names.length
        ? stats.systemSeries({
            customerId,
            workserver,
            stat: names,
            from: w.from,
            to: w.to,
            bucket: w.bucket,
          })
        : Promise.resolve([] as SystemSeries[]),
    [customerId, workserver, names.join('|'), w.from, w.to, w.bucket]
  );
  const from = Date.parse(w.from);
  const to = Date.parse(w.to);
  return (
    <section className="sx-body" data-testid="resources">
      <div className="sx-toolbar">
        <RangeTabs value={range} onChange={setRange} max="30d" />
        <span className="nyk-ui-hint">
          reported every 30 s; {w.bucket === '1h' ? 'hourly' : 'daily'} average and maximum
        </span>
      </div>
      <ErrorBox
        error={available.error ? statsError(available.error) : series.error ? statsError(series.error) : null}
      />
      <Loading loading={available.loading && !available.data} />
      {available.data && !names.length && (
        <EmptyState
          card
          title="No Sys: stats in this range."
          hint="The WorkServer reports them when its performance watcher collects system stats."
        />
      )}
      <div className={`sx-grid-2${series.loading ? ' oph-usage-stale' : ''}`}>
        {(series.data ?? []).map((s) => (
          <div className="nyk-card" key={s.stat}>
            <LineChart
              title={statName(s.stat)}
              series={[
                {
                  key: 'avg',
                  label: 'Average',
                  points: s.points.map((p) => ({ t: Date.parse(p.t), v: p.avg })),
                },
                { key: 'max', label: 'Max', points: s.points.map((p) => ({ t: Date.parse(p.t), v: p.max })) },
              ]}
              from={from}
              to={to}
              stepMs={w.bucket === '1h' ? 3_600_000 : 86_400_000}
              format={num}
              height={170}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function Regressions({ customerId, workserver }: { customerId: number; workserver: string }) {
  const [range, setRange] = useParam<'24h' | '7d'>('rrange', '7d');
  const w = useMemo(() => rangeWindow(range), [range]);
  const list = useAsync(
    () => stats.regressions({ customerId, workserver, from: w.from, to: w.to }),
    [customerId, workserver, w.from, w.to]
  );
  return (
    <section className="sx-body" data-testid="regressions">
      <div className="sx-toolbar">
        <Tabs
          value={range}
          onChange={setRange}
          options={[
            { value: '24h', label: '24 h' },
            { value: '7d', label: '7 d' },
          ]}
        />
        <span className="nyk-ui-hint">
          hourly p95 vs the median p95 of the same weekday and hour 1–4 weeks earlier
        </span>
      </div>
      <ErrorBox error={list.error ? statsError(list.error) : null} />
      <Loading loading={list.loading && !list.data} />
      <div className="nyk-card">
        {list.data && !list.data.length && <EmptyState title="No regressions in this range." />}
        {!!list.data?.length && (
          <TableScroller>
            <table className="nyk-ui-table nyk-cards">
              <thead>
                <tr>
                  <th>Method</th>
                  <th>Worst hour</th>
                  <th className="sx-num">p95</th>
                  <th className="sx-num">Baseline</th>
                  <th className="sx-num">Ratio</th>
                  <th className="sx-num">Regressed hours</th>
                  <th>Version change</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((r) => (
                  <tr key={r.method}>
                    <td className="nyk-card-title nyk-wrap">{r.method}</td>
                    <td data-label="Worst hour">{formatDateTime(r.hour)}</td>
                    <td data-label="p95" className="sx-num">
                      {fmtMs(r.currentP95)}
                    </td>
                    <td data-label="Baseline" className="sx-num">
                      {fmtMs(r.baselineP95)}
                    </td>
                    <td data-label="Ratio" className="sx-num">
                      ×{r.ratio.toFixed(1)}
                    </td>
                    <td data-label="Regressed hours" className="sx-num">
                      {r.regressedHours} / {r.evaluatedHours}
                    </td>
                    <td data-label="Version change">
                      {r.versionChange
                        ? `${r.versionChange.from ?? '?'} → ${r.versionChange.to} (${r.versionChange.since})`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        )}
      </div>
    </section>
  );
}

/** Deletes one snapshot (admin), e.g. a developer's test run against a newer WMS version sent by mistake. */
function DeleteDiagnostics({
  d,
  workserver,
  onDeleted,
  onError,
}: {
  d: Diagnostics;
  workserver: string;
  onDeleted: () => void;
  onError: (e: unknown) => void;
}) {
  const [busy, setBusy] = useState(false);
  const what = `${d.manual ? 'the manual' : 'the daily'} snapshot of ${formatDateTime(d.created)}`;
  return (
    <ConfirmButton
      size="sm"
      variant="danger"
      loading={busy}
      title={`Delete ${what}?`}
      message={
        `${workserver}, version ${d.version ?? '—'}. ` +
        (d.manual ? '' : "Its day's volumes are deleted with it. ") +
        "The installation's version and last-seen time follow the remaining snapshots. This cannot be undone."
      }
      confirmLabel="Delete"
      onConfirm={() => {
        setBusy(true);
        stats
          .deleteDiagnostics(d.id)
          .then(onDeleted, onError)
          .finally(() => setBusy(false));
      }}
    >
      Delete
    </ConfirmButton>
  );
}

/** The page of one installation's diagnostics snapshot. */
export const diagnosticsPath = (customerId: number, workserver: string, id: number) =>
  `/statistics/installations/${customerId}/${encodeURIComponent(workserver)}/diagnostics/${id}`;

/** One diagnostics snapshot (`…/diagnostics/:id`): the database part per engine, then the application part. */
export function DiagnosticsPage() {
  const { customerId: raw, workserver = '', id: rawId } = useParams();
  const customerId = Number(raw);
  const id = Number(rawId);
  const d = useAsync(() => stats.diagnosticsDetail(id), [id]);
  const canAdmin = hasScope(useHubLogin()?.scopes, SCOPES.statsAdmin);
  const navigate = useNavigate();
  const toast = useNotice();
  const back = `/statistics/installations/${customerId}/${encodeURIComponent(workserver)}?tab=diagnostics`;
  return (
    <Page
      title={
        <>
          <Link to={back} className="nyk-back">
            {workserver}
          </Link>
          <span className="nyk-crumb-sep"> / </span>
          Diagnostics {d.data ? formatDateTime(d.data.summary.created) : id}
        </>
      }
      description={
        d.data && (
          <dl className="sx-meta">
            <span>version {d.data.summary.version ?? '—'}</span>
            {d.data.summary.manual && <span>manual</span>}
          </dl>
        )
      }
      actions={
        canAdmin &&
        d.data && (
          <DeleteDiagnostics
            d={d.data.summary}
            workserver={workserver}
            onDeleted={() => navigate(back, { replace: true })}
            onError={(e) => toast.error(statsError(e))}
          />
        )
      }
    >
      {toast.element}
      <ErrorBox error={d.error} />
      <Loading loading={d.loading} />
      {d.data && (
        <>
          <div className="nyk-card">
            <h3>Database</h3>
            <DatabaseDiagnostics raw={d.data.database} />
          </div>
          <div className="nyk-card">
            <details className="sx-db-raw">
              <summary>Application</summary>
              <pre className="sx-pre">{JSON.stringify(d.data.application, null, 2)}</pre>
            </details>
          </div>
        </>
      )}
    </Page>
  );
}

/** One installation (organization + WorkServer): performance, resources, regressions, errors, diagnostics, versions. */
export function InstallationPage() {
  const { customerId: raw, workserver: ws } = useParams();
  const customerId = Number(raw);
  const workserver = ws ?? '';
  const [tab, setTab] = useParam<Tab>('tab', 'performance');
  const installations = useAsync(() => stats.installations({ customerId }), [customerId]);
  const canAdmin = hasScope(useHubLogin()?.scopes, SCOPES.statsAdmin);
  const toast = useNotice();
  const diagnostics = useAsync(
    () => stats.diagnostics({ customerId, workserver, limit: 200 }),
    [customerId, workserver]
  );
  const links = useStatsLinks();
  const inst = installations.data?.find((i) => i.workserver === workserver);
  const { envs, via } = envsForInstallation(links, customerId, workserver);
  const changes = versionChanges(diagnostics.data?.items ?? []);
  const markers: Marker[] = changes.map((c) => ({ t: Date.parse(c.at), label: c.to }));
  return (
    <Page
      title={workserver}
      description={
        <span className="sx-links">
          <OrgLink customerId={customerId} name={inst?.name ?? null} />
          {inst && <IngestStatus status={inst.status} monitoring idle={inst.idle} />}
          {inst?.version && <span>WMS {inst.version}</span>}
        </span>
      }
    >
      <ErrorBox error={installations.error} />
      {toast.element}
      <div className="nyk-card">
        {inst ? (
          <>
            <dl className="sx-meta">
              <span>
                Performance <b>{formatRelative(inst.lastSeen.performance)}</b>
              </span>
              <span>
                Sys stats <b>{formatRelative(inst.lastSeen.sys)}</b>
              </span>
              <span>
                Log <b>{formatRelative(inst.lastSeen.log)}</b>
              </span>
              <span>
                Daily metric <b>{formatRelative(inst.lastSeen.metric)}</b>
              </span>
              <span>
                First seen <b>{formatDateTime(inst.firstSeen)}</b>
              </span>
              <span>
                Usual hours{' '}
                <b title="Hours of the week with method timings on at least 2 of the last 4 weeks; outside them a quiet WorkServer is idle, not silent">
                  {inst.usualHours ? `${inst.usualHours} / week` : 'not learned yet'}
                </b>
              </span>
              {!!inst.silentKinds.length && (
                <span className="nyk-text-amber">stale: {inst.silentKinds.join(', ')}</span>
              )}
            </dl>
            <InstallationMarks
              inst={inst}
              canAdmin={canAdmin}
              onChanged={(next) =>
                installations.setData(
                  (all) => all?.map((i) => (i.workserver === next.workserver ? next : i)) ?? null
                )
              }
              onError={(e) => toast.error(statsError(e))}
            />
          </>
        ) : (
          installations.data && <p className="nyk-ui-hint">This WorkServer has no recorded activity.</p>
        )}
        <EnvLinks envs={envs} via={via} links={links} since={new Date(Date.now() - 3_600_000)} />
      </div>
      <Tabs value={tab} onChange={setTab} options={TABS} />
      {tab === 'performance' && (
        <PerformanceSection customerId={customerId} workserver={workserver} markers={markers} />
      )}
      {tab === 'resources' && <Resources customerId={customerId} workserver={workserver} />}
      {tab === 'regressions' && <Regressions customerId={customerId} workserver={workserver} />}
      {tab === 'errors' && <ErrorGroupsSection customerId={customerId} workserver={workserver} />}
      {tab === 'volumes' && <VolumesSection customerId={customerId} workserver={workserver} />}
      {tab === 'diagnostics' && (
        <section className="sx-body" data-testid="diagnostics">
          <ErrorBox error={diagnostics.error} />
          <div className="nyk-card">
            {diagnostics.data && !diagnostics.data.items.length && (
              <EmptyState
                title="No diagnostics snapshots."
                hint="Daily database diagnostics are kept for 14 days."
              />
            )}
            {!!diagnostics.data?.items.length && (
              <TableScroller>
                <table className="nyk-ui-table nyk-cards">
                  <thead>
                    <tr>
                      <th>Snapshot</th>
                      <th>Version</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {diagnostics.data.items.map((d) => (
                      <tr key={d.id}>
                        <td className="nyk-card-title">
                          {formatDateTime(d.created)}
                          {d.manual && <span className="nyk-ui-hint"> · manual</span>}
                        </td>
                        <td data-label="Version">{d.version ?? '—'}</td>
                        <td className="nyk-row-actions">
                          <Link
                            className="nyk-ui-btn nyk-ui-btn-ghost nyk-ui-btn-sm"
                            to={diagnosticsPath(customerId, workserver, d.id)}
                          >
                            Open
                          </Link>
                          {canAdmin && (
                            <DeleteDiagnostics
                              d={d}
                              workserver={workserver}
                              onDeleted={() => {
                                diagnostics.setData(
                                  (page) =>
                                    page && { ...page, items: page.items.filter((x) => x.id !== d.id) }
                                );
                                // version and last seen are recomputed by the collector
                                void installations.reload();
                                toast.success('Snapshot deleted.');
                              }}
                              onError={(e) => toast.error(statsError(e))}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            )}
          </div>
        </section>
      )}
      {tab === 'versions' && (
        <section className="sx-body" data-testid="versions">
          <div className="nyk-card">
            <p className="nyk-card-intro">
              Current: <b>{inst?.version ?? '—'}</b>. Changes found in the daily diagnostics (kept 14 days);
              they are marked on the performance charts.
            </p>
            {!changes.length ? (
              <EmptyState title="No version change in the kept diagnostics." />
            ) : (
              <TableScroller>
                <table className="nyk-ui-table nyk-cards">
                  <thead>
                    <tr>
                      <th>First seen</th>
                      <th>From</th>
                      <th>To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...changes].reverse().map((c) => (
                      <tr key={c.at}>
                        <td className="nyk-card-title">{formatDateTime(c.at)}</td>
                        <td data-label="From">{c.from}</td>
                        <td data-label="To">{c.to}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            )}
          </div>
        </section>
      )}
    </Page>
  );
}
